import { useAuth } from '@clerk/clerk-react'
import { useId, useState, type FormEvent } from 'react'

import { Button } from '@/components/ui/Button'
import { CountryField } from '@/components/ui/CountryField'
import { FIELD, FieldSelect, LABEL } from '@/components/ui/formFields'
import { GlassCard } from '@/components/ui/GlassCard'
import { Icon } from '@/components/ui/Icon'
import { Pill } from '@/components/ui/Pill'
import { useMe } from '@/context/MeProvider'
import { CATEGORIES, CATEGORY_OTHER } from '@/data/categories'
import { countryName } from '@/data/countries'
import { ApiError } from '@/lib/api/client'
import {
  removeMyListing,
  restoreMyListing,
  updateMyListing,
} from '@/lib/api/me'
import { cn } from '@/lib/cn'
import { Clock, accentForType, iconForCategory } from '@/lib/icons'
import { daysLeftFromExpiry, formatExpiry, toDatetimeLocal } from '@/lib/time'
import type { OwnerListing } from '@/lib/types/listing'

/** Matches api `listing_max_expiry_days`. */
const MAX_EXPIRY_DAYS = 30
const MS_PER_DAY = 24 * 60 * 60 * 1000

const STATUS_LABEL = {
  pending: 'Awaiting review',
  live: 'Live',
  quarantined_by_system: 'Under review',
  removed_by_user: 'Removed',
  taken_down_by_admin: 'Taken down',
} as const

function statusTone(
  listing: OwnerListing,
  daysLeft: number,
): 'neutral' | 'primary' | 'secondary' | 'error' {
  if (listing.linkCheckStatus === 'checking') return 'secondary'
  if (listing.status === 'live' && daysLeft <= 3) return 'secondary'
  if (listing.status === 'live') return 'primary'
  if (listing.status === 'quarantined_by_system' || listing.status === 'taken_down_by_admin') {
    return 'error'
  }
  return 'neutral'
}

function statusLabel(listing: OwnerListing, daysLeft: number): string {
  if (listing.linkCheckStatus === 'checking') return 'Checking link…'
  if (listing.status === 'live' && daysLeft <= 0) return 'Expired'
  if (listing.status === 'live' && daysLeft <= 3) return 'Expiring'
  return STATUS_LABEL[listing.status]
}

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function actionError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Sign in again to manage this link.'
    if (err.status === 403 && err.detail === 'Email not verified') {
      return 'Verify your email to manage links.'
    }
    if (err.status === 403 && err.detail === 'Account is not active') {
      return 'Your account is suspended.'
    }
    if (err.status === 409) return "This link can't be restored."
    return err.detail
  }
  return 'Something went wrong.'
}

function categorySelectValue(category: string | null): string {
  if (!category) return ''
  return (CATEGORIES as readonly string[]).includes(category)
    ? category
    : CATEGORY_OTHER
}

export function StashCard({
  listing,
  onChange,
}: {
  listing: OwnerListing
  onChange: (next: OwnerListing) => void
}) {
  const formId = useId()
  const { getToken } = useAuth()
  const { canMutate, messageFor } = useMe()
  const accent = accentForType(listing.type)
  const daysLeft = daysLeftFromExpiry(listing.expiresAt)
  const expiry = formatExpiry(listing.expiresAt)
  const headingId = `stash-${listing.id}`
  const place = countryName(listing.country)

  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [brand, setBrand] = useState(listing.brand)
  const [category, setCategory] = useState(
    categorySelectValue(listing.category),
  )
  const [categoryOther, setCategoryOther] = useState(
    categorySelectValue(listing.category) === CATEGORY_OTHER
      ? (listing.category ?? '')
      : '',
  )
  const [country, setCountry] = useState(listing.country ?? '')
  const [benefitClaim, setBenefitClaim] = useState(listing.benefitClaim ?? '')
  const [expiresAt, setExpiresAt] = useState(() =>
    toDatetimeLocal(new Date(listing.expiresAt)),
  )

  const canEdit = listing.status !== 'taken_down_by_admin'
  const canRemove =
    listing.status !== 'removed_by_user' &&
    listing.status !== 'taken_down_by_admin'
  const canRestore = listing.status === 'removed_by_user'

  async function requireMutate(): Promise<boolean> {
    const gate = messageFor('manage')
    if (gate) {
      setError(gate)
      return false
    }
    return true
  }

  function resetEdit() {
    setBrand(listing.brand)
    setCategory(categorySelectValue(listing.category))
    setCategoryOther(
      categorySelectValue(listing.category) === CATEGORY_OTHER
        ? (listing.category ?? '')
        : '',
    )
    setCountry(listing.country ?? '')
    setBenefitClaim(listing.benefitClaim ?? '')
    setExpiresAt(toDatetimeLocal(new Date(listing.expiresAt)))
    setError(null)
  }

  async function handleRemove() {
    setError(null)
    if (!(await requireMutate())) {
      return
    }
    setBusy(true)
    try {
      onChange(await removeMyListing(listing.id, getToken))
      setEditing(false)
    } catch (err) {
      setError(actionError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleRestore() {
    setError(null)
    if (!(await requireMutate())) {
      return
    }
    setBusy(true)
    try {
      onChange(await restoreMyListing(listing.id, getToken))
    } catch (err) {
      setError(actionError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    if (!(await requireMutate())) {
      return
    }

    const categoryValue =
      category === CATEGORY_OTHER
        ? blankToUndefined(categoryOther)
        : blankToUndefined(category)
    if (category === CATEGORY_OTHER && !categoryValue) {
      setError('Enter a category')
      return
    }

    const expiryDate = new Date(expiresAt)
    const now = Date.now()
    if (Number.isNaN(expiryDate.getTime()) || expiryDate.getTime() <= now) {
      setError('Expiry must be in the future')
      return
    }
    if (expiryDate.getTime() > now + MAX_EXPIRY_DAYS * MS_PER_DAY) {
      setError(`Expiry must be within ${MAX_EXPIRY_DAYS} days`)
      return
    }

    setBusy(true)
    try {
      onChange(
        await updateMyListing(
          listing.id,
          {
            brand: brand.trim(),
            category: categoryValue ?? null,
            country: blankToUndefined(country) ?? null,
            benefitClaim: blankToUndefined(benefitClaim) ?? null,
            expiresAt: expiryDate.toISOString(),
          },
          getToken,
        ),
      )
      setEditing(false)
    } catch (err) {
      setError(actionError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <GlassCard
      as="article"
      aria-labelledby={headingId}
      className="flex h-full flex-col gap-4 p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="border-glass-border bg-surface text-primary flex size-12 shrink-0 items-center justify-center rounded-lg border">
            <Icon icon={iconForCategory(listing.category)} className="size-5" />
          </span>
          <div className="min-w-0">
            <h3
              id={headingId}
              className="font-display text-fg truncate text-base font-bold"
            >
              {listing.brand}
            </h3>
            <p className="font-body text-body-sm text-fg-muted truncate">
              {listing.benefitClaim ?? listing.category ?? listing.type}
            </p>
          </div>
        </div>
        <Pill tone={statusTone(listing, daysLeft)}>
          {statusLabel(listing, daysLeft)}
        </Pill>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={accent}>{listing.type}</Pill>
        {place && (
          <span className="font-body text-label-sm text-fg-muted">{place}</span>
        )}
      </div>

      {listing.linkCheckStatus === 'failed' &&
        listing.status === 'pending' && (
          <p className="font-body text-body-sm text-fg-muted">
            We held this link for a closer look. It isn&apos;t on Discover yet.
          </p>
        )}

      <p className="font-body text-body-sm text-fg-muted break-all">
        <span className="text-label-caps uppercase">URL · </span>
        <a
          href={listing.destinationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary-fixed-dim underline underline-offset-2"
        >
          {listing.destinationUrl}
        </a>
      </p>

      <div className="border-glass-border bg-surface/50 flex items-center justify-between gap-3 rounded-lg border p-3">
        <div className="flex flex-col">
          <span className="font-body text-fg-muted text-[11px] uppercase">
            Claims
          </span>
          <span className="font-body text-fg text-body-sm font-semibold tabular-nums">
            {listing.claimCount}
            {listing.reportCount > 0 ? ` · ${listing.reportCount} reports` : ''}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="font-body text-fg-muted text-[11px] uppercase">
            Expires
          </span>
          <span
            className={cn(
              'font-body text-body-sm inline-flex items-center gap-1 font-semibold',
              expiry.urgent ? 'text-secondary' : 'text-fg',
            )}
          >
            <Icon icon={Clock} className="size-3.5" />
            {expiry.label}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Button
            type="button"
            variant="secondary"
            size="md"
            disabled={busy}
            onClick={() => {
              if (editing) {
                setEditing(false)
                resetEdit()
              } else if (!canMutate) {
                setError(messageFor('manage'))
              } else {
                setError(null)
                resetEdit()
                setEditing(true)
              }
            }}
          >
            {editing ? 'Cancel' : 'Edit'}
          </Button>
        )}
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="md"
            disabled={busy}
            onClick={() => {
              void handleRemove()
            }}
          >
            Remove
          </Button>
        )}
        {canRestore && (
          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={busy}
            onClick={() => {
              void handleRestore()
            }}
          >
            {busy ? 'Checking…' : 'Restore'}
          </Button>
        )}
      </div>

      {editing && (
        <form
          onSubmit={(event) => {
            void handleSave(event)
          }}
          className="border-glass-border space-y-4 border-t pt-4"
        >
          <div className="space-y-2">
            <label htmlFor={`${formId}-brand`} className={LABEL}>
              Brand
            </label>
            <input
              id={`${formId}-brand`}
              type="text"
              required
              value={brand}
              onChange={(event) => {
                setBrand(event.target.value)
              }}
              disabled={busy}
              className={FIELD}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor={`${formId}-category`} className={LABEL}>
                Category
              </label>
              <FieldSelect
                id={`${formId}-category`}
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value)
                }}
                disabled={busy}
              >
                <option value="">None</option>
                {CATEGORIES.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                <option value={CATEGORY_OTHER}>Other</option>
              </FieldSelect>
              {category === CATEGORY_OTHER && (
                <input
                  id={`${formId}-category-other`}
                  type="text"
                  required
                  value={categoryOther}
                  onChange={(event) => {
                    setCategoryOther(event.target.value)
                  }}
                  placeholder="Your category"
                  disabled={busy}
                  className={FIELD}
                />
              )}
            </div>
            <CountryField
              id={`${formId}-country`}
              value={country}
              onChange={setCountry}
              disabled={busy}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor={`${formId}-benefit`} className={LABEL}>
              The offer
            </label>
            <textarea
              id={`${formId}-benefit`}
              value={benefitClaim}
              onChange={(event) => {
                setBenefitClaim(event.target.value)
              }}
              rows={3}
              placeholder={
                listing.type === 'referral'
                  ? 'e.g. $20 off their first order (optional)'
                  : 'e.g. 15% off sitewide (optional)'
              }
              disabled={busy}
              className={cn(FIELD, 'min-h-24 resize-y')}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor={`${formId}-expires`} className={LABEL}>
              Expires
            </label>
            <input
              id={`${formId}-expires`}
              type="datetime-local"
              required
              min={toDatetimeLocal(new Date(Date.now() + 60_000))}
              max={toDatetimeLocal(
                new Date(Date.now() + MAX_EXPIRY_DAYS * MS_PER_DAY),
              )}
              value={expiresAt}
              onChange={(event) => {
                setExpiresAt(event.target.value)
              }}
              disabled={busy}
              className={FIELD}
            />
          </div>

          <Button type="submit" variant="gradient" size="md" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </form>
      )}

      {error && (
        <p className="font-body text-body-sm text-error" role="alert">
          {error}
        </p>
      )}
    </GlassCard>
  )
}
